import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, DollarSign, AlertTriangle, Download, ExternalLink, PartyPopper } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BUCKET_ORDER = ["current", "0-30", "31-60", "61-90", "90+"];
const BUCKET_COLOR = {
  "current": "#71717A",
  "0-30": "#16A34A",
  "31-60": "#F59E0B",
  "61-90": "#EA580C",
  "90+": "#DC2626",
};
const BUCKET_LABEL = {
  "current": "Not yet due",
  "0-30": "0-30 days",
  "31-60": "31-60 days",
  "61-90": "61-90 days",
  "90+": "90+ days",
};

export default function ARAging() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // bucket filter
  const [payDialog, setPayDialog] = useState(null);
  const [payForm, setPayForm] = useState({ paid_date: new Date().toISOString().slice(0, 10), paid_amount: "" });

  const load = async () => {
    try {
      const r = await api.get("/dashboards/ar-aging");
      setData(r.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const fmt = (n) => (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const openPay = (inv) => {
    setPayDialog(inv);
    setPayForm({
      paid_date: new Date().toISOString().slice(0, 10),
      paid_amount: String(inv.total_ttc),
      // Iter-211 — payment details captured at mark-paid time.
      payment_mode: "VIREMENT",
      payment_reference: "",
      payment_comment: "",
    });
  };

  const confirmPay = async () => {
    if (!payForm.payment_mode) { toast.error("Mode de paiement requis"); return; }
    if (!(payForm.payment_reference || "").trim()) { toast.error("Référence du paiement requise"); return; }
    try {
      await api.post(`/invoices/${payDialog.invoice_id}/mark-paid`, {
        paid_date: payForm.paid_date,
        paid_amount: parseFloat(payForm.paid_amount),
        payment_mode: payForm.payment_mode,
        payment_reference: (payForm.payment_reference || "").trim(),
        payment_comment: (payForm.payment_comment || "").trim() || null,
      });
      toast.success(`Invoice ${payDialog.number} marked as paid`);
      setPayDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.invoices;
    return data.invoices.filter((i) => i.bucket === filter);
  }, [data, filter]);

  if (!data) return <div className="p-12 text-zinc-500">Loading…</div>;

  const grandTotal = data.by_currency.reduce((acc, c) => acc + c.total, 0);
  const grandOverdue = data.by_currency.reduce((acc, c) =>
    acc + Object.entries(c.buckets).filter(([k]) => k !== "current").reduce((s, [, v]) => s + v.total, 0), 0);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="ar-aging-page">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Finance · {data.as_of}</div>
        <h1 className="font-heading text-5xl font-bold tracking-tight">AR aging</h1>
        <p className="text-zinc-500 mt-3 max-w-xl">Outstanding invoices — which clients are dragging their feet.</p>
      </div>

      {/* Per-currency aging summary */}
      <div className="grid grid-cols-1 gap-6 mb-10">
        {data.by_currency.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-sm text-zinc-500 inline-flex flex-col items-center gap-2 w-full" data-testid="ar-empty">
            <PartyPopper size={28} className="text-emerald-600" />
            <span>No outstanding invoices.</span>
          </div>
        )}
        {data.by_currency.map((c) => {
          const segments = BUCKET_ORDER.map((b) => ({
            key: b,
            total: c.buckets[b].total,
            count: c.buckets[b].count,
            pct: c.total ? (c.buckets[b].total / c.total) * 100 : 0,
          }));
          return (
            <div key={c.currency} className="bg-white border border-zinc-200 rounded-lg p-6" data-testid={`ar-currency-${c.currency}`}>
              <div className="flex flex-wrap gap-3 items-baseline justify-between mb-6">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">{c.currency}</div>
                  <div className="font-heading text-3xl font-bold tracking-tight">{fmt(c.total)} <span className="text-zinc-400">{c.currency}</span></div>
                  <div className="text-xs text-zinc-500 font-mono mt-1">{c.count} invoice{c.count > 1 ? "s" : ""}</div>
                </div>
              </div>

              {/* stacked aging bar */}
              <div className="flex items-center gap-1 h-3 rounded-full overflow-hidden bg-zinc-100">
                {segments.map((s) => s.pct > 0 && (
                  <div
                    key={s.key}
                    style={{ width: `${s.pct}%`, background: BUCKET_COLOR[s.key] }}
                    title={`${BUCKET_LABEL[s.key]}: ${fmt(s.total)} ${c.currency}`}
                    data-testid={`ar-bar-${c.currency}-${s.key}`}
                  />
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 mt-5">
                {segments.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setFilter(filter === s.key ? "all" : s.key)}
                    data-testid={`ar-bucket-${s.key}`}
                    className={`text-left p-3 rounded-md border transition-colors ${
                      filter === s.key ? "border-zinc-950 bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: BUCKET_COLOR[s.key] }} />
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{BUCKET_LABEL[s.key]}</span>
                    </div>
                    <div className="font-mono text-sm md:text-base font-bold tracking-tight mt-1 break-all">{fmt(s.total)}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{s.count} invoice{s.count > 1 ? "s" : ""}</div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length > 0 && (
        <>
          <div className="flex flex-wrap gap-3 items-baseline justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold tracking-tight">
              {filter === "all" ? "All outstanding" : `${BUCKET_LABEL[filter]} overdue`}
              <span className="ml-3 text-sm font-normal text-zinc-500 font-mono">{filtered.length}</span>
            </h2>
            {filter !== "all" && (
              <button onClick={() => setFilter("all")} data-testid="ar-filter-reset" className="text-sm text-zinc-700 underline underline-offset-4 decoration-zinc-300">Clear filter</button>
            )}
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg md:overflow-x-auto md:overflow-hidden">
            <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
              <div className="col-span-2">Invoice</div>
              <div className="col-span-3">Client</div>
              <div className="col-span-2">Due</div>
              <div className="col-span-1">Overdue</div>
              <div className="col-span-2 text-right">TTC</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {filtered.map((i) => (
              <div key={i.invoice_id} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-5 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`ar-row-${i.invoice_id}`}>
                <div className="col-span-2 font-mono font-medium text-sm">
                  <div className="flex items-center gap-2">
                    {i.number}
                    {i.drive_web_view_link && <a href={i.drive_web_view_link} target="_blank" rel="noreferrer" className="text-blue-700"><ExternalLink size={12} /></a>}
                  </div>
                  <div className="text-[10px] text-zinc-500">{i.mission_title}</div>
                </div>
                <div className="col-span-3 text-sm truncate">{i.client_name}</div>
                <div className="col-span-2 text-sm font-mono text-zinc-600">{i.due_date}</div>
                <div className="col-span-1">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider" style={{ background: BUCKET_COLOR[i.bucket] + "22", color: BUCKET_COLOR[i.bucket] }}>
                    {i.days_overdue < 0 ? `${-i.days_overdue}d left` : `${i.days_overdue}d`}
                  </span>
                </div>
                <div className="col-span-2 text-right font-mono font-medium">{fmt(i.total_ttc)} {i.currency}</div>
                <div className="col-span-2 text-right flex items-center gap-2 justify-end">
                  <button
                    onClick={async () => {
                      // Iter-122: authenticated blob download instead of window.open (which
                      // would drop the bearer token → 'Not authenticated').
                      try {
                        const r = await api.get(`/invoices/${i.invoice_id}/pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                        window.open(url, "_blank");
                        setTimeout(() => URL.revokeObjectURL(url), 30000);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Cannot load PDF"); }
                    }}
                    className="inline-flex items-center gap-1 px-2 h-8 border border-zinc-200 text-xs rounded-md hover:bg-white"
                  >
                    <Download size={12} />
                  </button>
                  <button
                    onClick={() => openPay(i)}
                    data-testid={`ar-mark-paid-${i.invoice_id}`}
                    className="inline-flex items-center gap-1 px-3 h-8 bg-zinc-950 text-white text-xs rounded-md hover:bg-zinc-800"
                  >
                    <Check size={12} /> Paid
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mark {payDialog?.number} as paid</DialogTitle>
            <DialogDescription>
              Détails du paiement — archivés sur la facture et visibles dans le registre des factures payées.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date opération <span className="text-red-600">*</span></Label>
                <Input type="date" data-testid="pay-date" value={payForm.paid_date} onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })} />
              </div>
              <div>
                <Label>Montant payé ({payDialog?.currency}) <span className="text-red-600">*</span></Label>
                <Input type="number" step="0.01" data-testid="pay-amount" value={payForm.paid_amount} onChange={(e) => setPayForm({ ...payForm, paid_amount: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Mode de paiement <span className="text-red-600">*</span></Label>
              <select
                value={payForm.payment_mode || "VIREMENT"}
                onChange={(e) => setPayForm({ ...payForm, payment_mode: e.target.value })}
                data-testid="pay-mode"
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm"
              >
                <option value="VIREMENT">Virement</option>
                <option value="CHEQUE">Chèque</option>
                <option value="ESPECES">Espèces</option>
                <option value="CARTE">Carte</option>
                <option value="PRELEVEMENT">Prélèvement</option>
                <option value="AUTRE">Autre</option>
              </select>
            </div>
            <div>
              <Label>Référence (N° virement, chèque, SWIFT…) <span className="text-red-600">*</span></Label>
              <Input
                value={payForm.payment_reference || ""}
                onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })}
                placeholder="VIR-202605-001"
                data-testid="pay-reference"
                maxLength={120}
              />
            </div>
            <div>
              <Label>Commentaire (optionnel)</Label>
              <textarea
                value={payForm.payment_comment || ""}
                onChange={(e) => setPayForm({ ...payForm, payment_comment: e.target.value })}
                placeholder="Ex: paiement partiel — solde le 15/06"
                data-testid="pay-comment"
                maxLength={1000}
                rows={2}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm resize-y"
              />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setPayDialog(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={confirmPay} data-testid="pay-confirm" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md inline-flex items-center gap-2"><Check size={14} /> Confirm payment</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
